// Écran « Campagnes » (chantier campagne) : DEUX overlays DOM pilotés par ce module.
//   #campaigns      : la LISTE (nom, auteur, meilleur temps solo, réussites coop) avec
//                     « Jouer (solo) », « Jouer à 2 », « Supprimer » (auteur/admin) et
//                     « + Nouvelle campagne ».
//   #campaign-edit  : le CRÉATEUR (nom + composition ORDONNÉE de 5 niveaux : colonne
//                     « disponibles » = catalogue + cartes de la famille, colonne « choisis »
//                     avec monter / descendre / retirer).
// Un seul overlay visible à la fois ; teardown() masque les deux et détache TOUT (écouteurs
// symétriques, listes vidées) : entrer / sortir plusieurs fois ne laisse aucune fuite.
//
// Sécurité (même règle que my-levels-ui / online-ui) : tout texte d'origine serveur ou
// utilisateur (noms de campagnes, titres de cartes, auteurs, pseudos, messages) est inséré
// via textContent, JAMAIS innerHTML. Les noms n'ont pas de filtre de charset côté serveur :
// un nom piégé ne doit jamais devenir du HTML (risque de XSS stocké). textContent d'une
// chaîne concaténée (« par {author} », « (pseudo) ») est sûr : il n'interprète aucun HTML.
//
// Jouabilité : le créateur ne propose que des cartes JOUABLES (une campagne doit se finir de
// bout en bout). apiLibrary renvoie des LevelSummary SANS la grille : on charge donc chaque
// carte famille (apiGetLevel) et on ne garde que celles qui passent validateLevel (comme
// online-ui pour hasSpawnP2). Le catalogue (TEST_LEVELS) est toujours jouable. Le serveur
// revalide à l'enregistrement (défense en profondeur, « Niveau invalide dans la campagne »).
//
// Jouer : onPlaySolo / onPlayCoop sont OPTIONNELS. Tant qu'ils ne sont pas fournis (état
// actuel du chantier), un clic « Jouer » affiche un statut « bientôt » et reste sur la liste.
// Task 4 fournira onPlaySolo (lancement solo), Task 6 onPlayCoop (coop en ligne). Quand un
// callback est fourni, ce module fait teardown() PUIS l'appelle (l'overlay libère la place à
// la partie), exactement comme my-levels-ui rend la main à l'éditeur.
import type { CampaignFull, CampaignSummary, MeResponse } from '../shared/api-types'
import { ENTREES_ATTRIBUABLES, entreeById } from '../shared/entrees'
import { levelFromAscii, validateLevel } from '../shared/level'
import { TEST_LEVELS } from '../shared/levels-catalog'
import { apiCreateCampaign, apiDeleteCampaign, apiGetCampaign, apiGetLevel, apiLibrary, apiListCampaigns, apiSetCampaignEntree } from './api'
import { errText, mkById } from './util'
import { formatTime } from './format-time'

export interface CampaignsOpts {
  // canvas / sheet ne sont pas lus ici (écran 100 % DOM) : présents pour l'homogénéité des
  // écrans et pour les callbacks de lancement de T4/T6 (qui utilisent, eux, le canvas du menu).
  canvas: HTMLCanvasElement
  sheet: HTMLImageElement
  me: MeResponse | null // null possible via le crochet dev ?campaigns : « Supprimer » masqué
  onPlaySolo?: (full: CampaignFull) => void // lance la campagne en solo (toujours 1 joueur)
  onPlayCoop?: (full: CampaignFull, players: number) => void // coop en ligne à N (2 ou 3, base N T8)
  onPlayVersus?: (full: CampaignFull, players: number) => void // duel en ligne à N (2 ou 3, base N T8)
  onQuit: () => void // retour au menu principal (bouton Retour / touche Échap)
}
export interface CampaignsHandle { stop(): void }

/** Nombre EXACT de niveaux d'une campagne (verrouillé produit, miroir du serveur). */
const CAMPAIGN_SIZE = 5

/** Un niveau proposé au créateur : slug d'API + titre affiché (catalogue ou carte famille). */
interface Pick { slug: string; title: string }

export function startCampaigns(opts: CampaignsOpts): CampaignsHandle {
  const byId = mkById('Campagnes')
  // Liste
  const listRoot = byId('campaigns')
  const listEl = byId('cp-list')
  const errorEl = byId('cp-error')
  const statusEl = byId('cp-status')
  const newBtn = byId<HTMLButtonElement>('cp-new')
  const backBtn = byId<HTMLButtonElement>('cp-back')
  // Créateur
  const editRoot = byId('campaign-edit')
  const nameInput = byId<HTMLInputElement>('ce-name')
  const ceStatus = byId('ce-status')
  const availEl = byId('ce-available')
  const chosenEl = byId('ce-chosen')
  const chosenTitle = byId('ce-chosen-title')
  const saveBtn = byId<HTMLButtonElement>('ce-save')
  const cancelBtn = byId<HTMLButtonElement>('ce-cancel')
  const editBackBtn = byId<HTMLButtonElement>('ce-back')

  // LA RÉCOMPENSE (01/08). Le créateur d'une campagne choisit l'ENTRÉE EN SCÈNE que gagnera
  // quiconque la finira en solo, de bout en bout (shared/entrees.ts).
  //
  // ⚠️ Aucun contrôle de pseudo nulle part, et c'est volontaire : le droit vient de ce qu'on est
  // l'AUTEUR de la campagne qu'on crée. Le serveur ne peut pas se tromper là-dessus — il écrit la
  // récompense sur la campagne qu'il vient de créer au nom de l'appelant — et personne n'est
  // enfermé dehors par un nom écrit en dur.
  //
  // Construit ici plutôt que dans public/index.html : un écran s'explique mieux à côté de son
  // code, et le HTML du créateur est partagé avec d'autres chantiers.
  const recompenseWrap = document.createElement('label')
  recompenseWrap.style.cssText = 'display:block;margin:10px 0 2px;font-size:12px;color:#a49dbe'
  recompenseWrap.textContent = 'Récompense (entrée en scène, gagnée en finissant la campagne en solo)'
  const recompenseSel = document.createElement('select')
  recompenseSel.style.cssText = 'display:block;width:100%;margin-top:5px;padding:8px 10px;border-radius:9px;border:1px solid #35305e;background:#1d1840;color:#efecf9;font-family:inherit;font-size:13px'
  const rien = document.createElement('option')
  rien.value = ''; rien.textContent = 'Aucune récompense'
  recompenseSel.appendChild(rien)
  for (const e of ENTREES_ATTRIBUABLES) {
    const o = document.createElement('option')
    o.value = e.id; o.textContent = e.nom
    recompenseSel.appendChild(o)
  }
  recompenseWrap.appendChild(recompenseSel)
  nameInput.parentElement?.insertBefore(recompenseWrap, nameInput.nextSibling)

  let stopped = false
  // Une seule action réseau à la fois (chargement liste, jouer, supprimer, enregistrer) :
  // évite les doubles clics et les actions concurrentes en vol.
  let busy = false
  let view: 'list' | 'creator' = 'list'
  // Composition en cours du créateur (ordonnée) et cartes famille chargées (le catalogue,
  // lui, vient de TEST_LEVELS, toujours disponible).
  let chosen: Pick[] = []
  let familyCards: Pick[] = []
  // Jeton d'ouverture du créateur : une réponse apiLibrary tardive d'une session précédente
  // ne doit pas re-rendre la colonne d'une nouvelle session (ou après teardown).
  let creatorToken = 0
  // SÉCURITÉ : textContent (jamais innerHTML) pour tous les messages, qui peuvent reprendre
  // une saisie utilisateur (message serveur, nom de carte...).
  function showError(msg: string): void { errorEl.textContent = msg }
  function setStatus(msg: string): void { statusEl.textContent = msg }
  function setListMessage(text: string): void {
    listEl.replaceChildren()
    const p = document.createElement('p')
    p.className = 'cp-empty'
    p.textContent = text
    listEl.appendChild(p)
  }

  // ---- LISTE ---------------------------------------------------------------

  // Recharge la liste depuis le serveur et la reconstruit (ouverture + retour de création +
  // après suppression) pour refléter l'état réel côté serveur.
  async function refresh(): Promise<void> {
    showError('')
    setListMessage('Chargement...')
    let camps: CampaignSummary[]
    try {
      camps = await apiListCampaigns()
    } catch (e) {
      if (stopped) return
      showError(errText(e))
      setListMessage('Impossible de charger les campagnes. Réessaie.')
      return
    }
    if (stopped) return
    listEl.replaceChildren()
    if (camps.length === 0) {
      setListMessage('Aucune campagne pour l\'instant. Clique sur « + Nouvelle campagne » pour en créer une.')
      return
    }
    for (const camp of camps) listEl.appendChild(buildRow(camp))
  }

  // Une ligne de campagne : nom + auteur, niveaux, meilleur temps solo / réussites coop, actions.
  function buildRow(camp: CampaignSummary): HTMLElement {
    const row = document.createElement('div')
    row.className = 'cp-row'

    const main = document.createElement('div')
    main.className = 'cp-row-main'
    const name = document.createElement('span')
    name.className = 'cp-name'
    name.textContent = camp.name // SÉCURITÉ : nom d'origine utilisateur
    const meta = document.createElement('span')
    meta.className = 'cp-meta'
    meta.textContent = `par ${camp.author}` // SÉCURITÉ : pseudo via textContent
    main.append(name, meta)
    row.appendChild(main)

    const levels = document.createElement('p')
    levels.className = 'cp-levels'
    levels.textContent = `Niveaux : ${camp.levelTitles.join(' · ')}` // SÉCURITÉ : titres de cartes
    row.appendChild(levels)

    const stats = document.createElement('p')
    stats.className = 'cp-stats'
    const solo = camp.soloBest
      ? `Meilleur solo : ${formatTime(camp.soloBest.bestMs)} (${camp.soloBest.pseudo})`
      : 'Meilleur solo : aucun'
    const coop = `Coop : ${camp.coopWins} réussite${camp.coopWins > 1 ? 's' : ''}`
    stats.textContent = `${solo} · ${coop}`
    row.appendChild(stats)

    const actions = document.createElement('div')
    actions.className = 'cp-row-actions'
    const soloBtn = mkButton('cp-act cp-act-play', 'Jouer (solo)')
    soloBtn.addEventListener('click', () => void play('solo', camp))
    const coopBtn = mkButton('cp-act cp-act-play', 'Coop à 2')
    coopBtn.addEventListener('click', () => void play('coop', camp))
    const versusBtn = mkButton('cp-act cp-act-play', 'Duel à 2')
    versusBtn.addEventListener('click', () => void play('versus', camp))
    actions.append(soloBtn, coopBtn, versusBtn)
    // « Supprimer » visible pour l'auteur ou un admin (le serveur reste le garde : un non-auteur
    // reçoit un 404 discret). me peut être null via le crochet dev : on masque alors le bouton.
    if (opts.me && (camp.author === opts.me.pseudo || opts.me.role === 'admin')) {
      const delBtn = mkButton('cp-act cp-act-danger', 'Supprimer')
      delBtn.addEventListener('click', () => void del(camp, delBtn))
      actions.appendChild(delBtn)
    }
    row.appendChild(actions)

    // LA RÉCOMPENSE, MODIFIABLE APRÈS COUP. L'auteur (ou un admin) la choisit ici, sur une
    // campagne qui existe déjà, sans avoir à la supprimer — ce qui emporterait les temps de toute
    // la famille avec elle. Pour les autres joueurs, la même ligne dit seulement ce qu'il y a à
    // gagner : c'est une information, pas un réglage.
    const recompense = document.createElement('div')
    recompense.className = 'cp-row-stats'
    const estMien = !!opts.me && (camp.author === opts.me.pseudo || opts.me.role === 'admin')
    if (estMien) {
      const lab = document.createElement('span')
      lab.textContent = '🎬 Récompense : '
      const sel = document.createElement('select')
      sel.style.cssText = 'margin-left:4px;padding:4px 8px;border-radius:8px;border:1px solid #35305e;background:#1d1840;color:#efecf9;font-family:inherit;font-size:12px'
      const rien = document.createElement('option')
      rien.value = ''; rien.textContent = 'aucune'
      sel.appendChild(rien)
      for (const e of ENTREES_ATTRIBUABLES) {
        const o = document.createElement('option')
        o.value = e.id; o.textContent = e.nom
        sel.appendChild(o)
      }
      sel.value = camp.entree ?? ''
      const dit = document.createElement('span')
      dit.style.cssText = 'margin-left:8px;font-size:12px;color:#6fd08c'
      sel.addEventListener('change', () => {
        const v = sel.value || null
        sel.disabled = true
        dit.textContent = '...'
        apiSetCampaignEntree(camp.id, v)
          .then(() => { camp.entree = v; dit.textContent = v ? '✓ enregistré' : '✓ retirée' })
          .catch((e: unknown) => { dit.style.color = '#ff6b5f'; dit.textContent = errText(e); sel.value = camp.entree ?? '' })
          .finally(() => { sel.disabled = false })
      })
      recompense.append(lab, sel, dit)
    } else {
      const e = camp.entree ? entreeById(camp.entree) : undefined
      recompense.textContent = e
        ? `🎬 À gagner en la finissant en solo : ${e.nom}`
        : '🎬 Aucune récompense'
    }
    row.appendChild(recompense)
    return row
  }

  // Nombre de joueurs choisi pour une campagne en ligne (base N, T8) : 2 (défaut) ou 3, lu du
  // groupe radio #cp-players de l'en-tête. Le solo l'ignore (toujours 1 joueur).
  function selectedPlayers(): number {
    const checked = document.querySelector('input[name="cp-players"]:checked') as HTMLInputElement | null
    return checked?.value === '3' ? 3 : 2
  }

  // Jouer : charge la campagne complète (valide aussi qu'elle existe encore) puis, si le
  // callback est fourni (T4 solo / T6 coop / duel), rend la main pour lancer la partie ; sinon
  // (état actuel) affiche un statut « bientôt » et reste sur la liste.
  async function play(kind: 'solo' | 'coop' | 'versus', camp: CampaignSummary): Promise<void> {
    if (busy) return
    busy = true
    showError('')
    setStatus('Chargement...')
    let full: CampaignFull
    try {
      full = await apiGetCampaign(camp.id)
    } catch (e) {
      if (!stopped) { setStatus(''); showError(errText(e)) }
      busy = false
      return
    }
    if (stopped) { busy = false; return }
    busy = false
    if (kind === 'solo') {
      if (opts.onPlaySolo) { teardown(); opts.onPlaySolo(full); return }
    } else {
      // Coop / duel en ligne (base N, T8) : on transmet le nombre de joueurs choisi (2/3). Une
      // campagne à 3 exige que TOUS ses niveaux aient un départ Joueur 3 ; sinon le serveur refuse
      // à la création et le lobby affiche l'erreur telle quelle (rien à filtrer côté client ici).
      const cb = kind === 'coop' ? opts.onPlayCoop : opts.onPlayVersus
      if (cb) { teardown(); cb(full, selectedPlayers()); return }
    }
    setStatus(
      kind === 'solo'
        ? 'Le mode solo arrive bientôt.'
        : kind === 'coop'
          ? 'La coop à deux arrive bientôt.'
          : 'Le duel à deux arrive bientôt.',
    )
  }

  // Supprimer : confirmation native (action définitive) puis suppression + rechargement.
  async function del(camp: CampaignSummary, btn: HTMLButtonElement): Promise<void> {
    if (busy) return
    if (!confirm(`Supprimer la campagne « ${camp.name} » ? Cette action est définitive.`)) return
    busy = true
    showError('')
    setStatus('')
    btn.disabled = true
    try {
      await apiDeleteCampaign(camp.id)
      if (stopped) return
      await refresh()
    } catch (e) {
      if (stopped) return
      showError(errText(e))
      btn.disabled = false
    } finally {
      busy = false
    }
  }

  // ---- CRÉATEUR ------------------------------------------------------------

  function setCeStatus(msg: string, kind: 'info' | 'warn' | 'error'): void {
    ceStatus.textContent = msg
    ceStatus.className = 'ce-status' + (kind === 'error' ? ' is-error' : kind === 'warn' ? ' is-warn' : '')
  }

  // Recompose l'UI après tout changement : colonnes, compteur, état du bouton Enregistrer,
  // et indice de progression. « Enregistrer » n'est actif QU'À exactement 5 niveaux.
  function afterComposition(): void {
    renderAvailable()
    renderChosen()
    chosenTitle.textContent = `Choisis (${chosen.length}/${CAMPAIGN_SIZE})`
    saveBtn.disabled = chosen.length !== CAMPAIGN_SIZE
    const left = CAMPAIGN_SIZE - chosen.length
    setCeStatus(
      left <= 0
        ? 'Campagne complète. Donne-lui un nom puis enregistre.'
        : `Encore ${left} niveau${left > 1 ? 'x' : ''} à choisir.`,
      'info',
    )
  }

  // Colonne « disponibles » : catalogue PUIS cartes de la famille. « Ajouter » désactivé
  // quand la composition atteint 5 (on ne peut pas dépasser). Les doublons sont permis
  // (le serveur les accepte) : une carte déjà choisie reste proposée.
  function renderAvailable(): void {
    availEl.replaceChildren()
    const full = chosen.length >= CAMPAIGN_SIZE
    const addGroup = (title: string, items: Pick[]): void => {
      const h = document.createElement('p')
      h.className = 'ce-group'
      h.textContent = title
      availEl.appendChild(h)
      for (const item of items) {
        const rowEl = document.createElement('div')
        rowEl.className = 'ce-item'
        const nm = document.createElement('span')
        nm.className = 'ce-item-name'
        nm.textContent = item.title // SÉCURITÉ : nom de carte famille via textContent
        const add = mkButton('ce-mini', 'Ajouter')
        add.disabled = full
        add.addEventListener('click', () => addLevel(item))
        rowEl.append(nm, add)
        availEl.appendChild(rowEl)
      }
    }
    addGroup('Catalogue', TEST_LEVELS.map((l) => ({ slug: l.slug, title: l.title })))
    if (familyCards.length > 0) addGroup('Cartes de la famille', familyCards)
  }

  // Colonne « choisis » : liste ORDONNÉE avec position, monter, descendre, retirer.
  function renderChosen(): void {
    chosenEl.replaceChildren()
    if (chosen.length === 0) {
      const p = document.createElement('p')
      p.className = 'ce-empty'
      p.textContent = 'Aucun niveau choisi. Ajoute des niveaux depuis la colonne de gauche.'
      chosenEl.appendChild(p)
      return
    }
    chosen.forEach((item, i) => {
      const rowEl = document.createElement('div')
      rowEl.className = 'ce-chosen-row'
      const pos = document.createElement('span')
      pos.className = 'ce-pos'
      pos.textContent = `${i + 1}.`
      const nm = document.createElement('span')
      nm.className = 'ce-item-name'
      nm.textContent = item.title // SÉCURITÉ : titre via textContent
      const acts = document.createElement('div')
      acts.className = 'ce-chosen-acts'
      const up = mkButton('ce-mini', '↑')
      up.setAttribute('aria-label', 'Monter')
      up.disabled = i === 0
      up.addEventListener('click', () => moveLevel(i, -1))
      const down = mkButton('ce-mini', '↓')
      down.setAttribute('aria-label', 'Descendre')
      down.disabled = i === chosen.length - 1
      down.addEventListener('click', () => moveLevel(i, 1))
      const rm = mkButton('ce-mini ce-mini-danger', 'Retirer')
      rm.addEventListener('click', () => removeLevel(i))
      acts.append(up, down, rm)
      rowEl.append(pos, nm, acts)
      chosenEl.appendChild(rowEl)
    })
  }

  function addLevel(item: Pick): void {
    if (chosen.length >= CAMPAIGN_SIZE) return
    chosen.push({ slug: item.slug, title: item.title })
    afterComposition()
  }
  function removeLevel(i: number): void {
    chosen.splice(i, 1)
    afterComposition()
  }
  function moveLevel(i: number, dir: -1 | 1): void {
    const j = i + dir
    if (j < 0 || j >= chosen.length) return
    const tmp = chosen[i]!
    chosen[i] = chosen[j]!
    chosen[j] = tmp
    afterComposition()
  }

  // Enregistrer : le bouton n'est actif qu'à 5 niveaux ; on valide le nom localement (feedback
  // immédiat, pas d'aller-retour serveur inutile), puis on POST. En cas de refus serveur, on
  // affiche son message tel quel (ex. « Niveau invalide dans la campagne », « Nom invalide »).
  async function save(): Promise<void> {
    if (busy) return
    if (chosen.length !== CAMPAIGN_SIZE) return
    const name = nameInput.value.trim()
    if (name.length === 0) { setCeStatus('Donne un nom à ta campagne.', 'warn'); nameInput.focus(); return }
    busy = true
    saveBtn.disabled = true
    setCeStatus('Enregistrement...', 'info')
    let ok = false
    try {
      await apiCreateCampaign(name, chosen.map((c) => c.slug), recompenseSel.value || null)
      ok = true
    } catch (e) {
      if (!stopped) { setCeStatus(errText(e), 'error'); saveBtn.disabled = false }
    }
    busy = false
    if (stopped) return
    if (ok) showList() // retour à la liste : le refresh y montre la nouvelle campagne
  }

  // ---- NAVIGATION entre les deux vues --------------------------------------

  function showList(): void {
    view = 'list'
    editRoot.hidden = true
    listRoot.hidden = false
    showError('')
    setStatus('')
    void refresh()
  }

  function openCreator(): void {
    if (busy) return
    view = 'creator'
    const token = ++creatorToken
    nameInput.value = ''
    recompenseSel.value = ''
    chosen = []
    familyCards = []
    listRoot.hidden = true
    editRoot.hidden = false
    afterComposition() // rend colonnes + compteur + bouton + indice
    nameInput.focus()
    // Cartes de la famille : on ne propose que les cartes JOUABLES (une campagne doit se finir
    // de bout en bout ; refuser au POST une carte inachevée donnerait une erreur générique peu
    // claire). apiLibrary renvoie des LevelSummary SANS grille, on charge donc chaque carte pour
    // tester validateLevel (comme online-ui pour hasSpawnP2). Hors ligne / erreur : catalogue
    // seul. Le jeton écarte une réponse tardive après changement de vue ou teardown.
    apiLibrary()
      .then(async (libs) => {
        const fulls = await Promise.all(libs.map((l) => apiGetLevel(l.id).catch(() => null)))
        if (stopped || token !== creatorToken) return
        const playable = (rows: string[]): boolean => {
          try { return validateLevel(levelFromAscii('', rows)).length === 0 } catch { return false }
        }
        familyCards = fulls
          .filter((f): f is NonNullable<typeof f> => f !== null && playable(f.ascii.split('\n')))
          .map((f) => ({ slug: `fam-${f.id}`, title: f.name }))
        renderAvailable()
      })
      .catch(() => {})
  }

  // ---- Écouteurs STATIQUES (attachés une fois, retirés au teardown) --------

  function onListBack(): void { teardown(); opts.onQuit() }
  function onNew(): void { openCreator() }
  function onCreatorBack(): void { showList() } // Retour / Annuler du créateur = retour à la liste
  function onSave(): void { void save() }
  function onKey(e: KeyboardEvent): void {
    if (e.code !== 'Escape') return
    e.preventDefault()
    if (view === 'creator') showList()
    else { teardown(); opts.onQuit() }
  }

  function teardown(): void {
    if (stopped) return
    stopped = true
    newBtn.removeEventListener('click', onNew)
    backBtn.removeEventListener('click', onListBack)
    editBackBtn.removeEventListener('click', onCreatorBack)
    cancelBtn.removeEventListener('click', onCreatorBack)
    saveBtn.removeEventListener('click', onSave)
    removeEventListener('keydown', onKey)
    listEl.replaceChildren()
    availEl.replaceChildren()
    chosenEl.replaceChildren()
    showError('')
    setStatus('')
    listRoot.hidden = true
    editRoot.hidden = true
  }

  // Petit fabricant de bouton typé (les boutons dynamiques sont recréés à chaque rendu et
  // libérés au replaceChildren : leurs écouteurs partent avec eux, aucune fuite à gérer).
  function mkButton(className: string, label: string): HTMLButtonElement {
    const b = document.createElement('button')
    b.type = 'button'
    b.className = className
    b.textContent = label
    return b
  }

  newBtn.addEventListener('click', onNew)
  backBtn.addEventListener('click', onListBack)
  editBackBtn.addEventListener('click', onCreatorBack)
  cancelBtn.addEventListener('click', onCreatorBack)
  saveBtn.addEventListener('click', onSave)
  addEventListener('keydown', onKey)
  showList() // on ouvre toujours sur la liste
  return { stop: teardown }
}
